import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';

import Spinner from '~/components/Common/Spinner';

import { useCreateFacebookContentTemplate } from '~/hooks/apis/public-offer-feed/useFacebookContentTemplates';

import { ASPECT_RATIO_OPTIONS, AspectRatio } from '~/services/PublicOfferFeedService';

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function CreateTemplateModal({ open, onClose }: Props) {
  const [creatomateId, setCreatomateId] = useState('');
  const [name, setName] = useState('');
  const [aspectRatio, setAspectRatio] = useState<AspectRatio | ''>('');
  const { enqueueSnackbar } = useSnackbar();
  const createMutation = useCreateFacebookContentTemplate();

  const handleSubmit = async () => {
    if (!creatomateId || !name || !aspectRatio) {
      enqueueSnackbar('Please fill in all required fields', { variant: 'error' });
      return;
    }

    try {
      const payload = {
        creatomate_id: creatomateId,
        name,
        aspect_ratio: aspectRatio as AspectRatio,
      };

      await createMutation.mutateAsync(payload);
      enqueueSnackbar('Template created successfully', { variant: 'success' });
      handleClose();
    } catch (error) {
      enqueueSnackbar(`Failed to create template: ${(error as Error).message}`, { variant: 'error' });
    }
  };

  const handleClose = () => {
    setCreatomateId('');
    setName('');
    setAspectRatio('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Facebook Content Template</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            autoFocus
            fullWidth
            label="Creatomate ID"
            required
            value={creatomateId}
            onChange={(e) => setCreatomateId(e.target.value)}
            placeholder="Enter creatomate template ID"
          />
          <TextField
            fullWidth
            label="Name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Template name"
          />
          <FormControl fullWidth required>
            <InputLabel>Aspect Ratio</InputLabel>
            <Select value={aspectRatio} label="Aspect Ratio" onChange={(e) => setAspectRatio(e.target.value)}>
              {ASPECT_RATIO_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={handleClose} disabled={createMutation.isPending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={createMutation.isPending}>
          {createMutation.isPending ? <Spinner size={15} inline /> : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
